// Dice a caster's or conjurer's sheet adds by itself (issue 1172; docs/MRCS/shadowrun-3.md §15 "For the table"): where
// the table's system declares them (`spellcasting.magic_dice`), a sheet's reference field (the caster's totem, its
// element) names an entry whose numbers give dice on a casting or a conjuring, each shown as its own line ("Bear: +2
// dice (health spells)"), a drawback as dice taken off, and a target-number line. Every key below is the system's data;
// nothing here names a game. The server reads the entries (`MagicSource`); the form works out the lines from them
// (`magicDiceLines`), each with the switch that drops it for one test; the server checks their shape and bounds.
const KEY_RE = /^[a-z][a-z0-9_]{0,62}$/;
// eslint-disable-next-line no-control-regex
const LINE_BAD = /[\u0000-\u001f\u007f]/;
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const isInt = (v, min, max) => typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max;
const isText = (v, max) => typeof v === 'string' && v.trim().length >= 1 && v.length <= max && !LINE_BAD.test(v);
const isKey = (v) => typeof v === 'string' && KEY_RE.test(v);
export const DICE_TERM_LIMITS = { count: 6, value: 6, label: 40, note: 60 };
/** Dice lines off the wire or a record: exact shape or null; absent is none. */
export function parseDiceTerms(v) {
    if (v === undefined)
        return [];
    if (!Array.isArray(v) || v.length > DICE_TERM_LIMITS.count)
        return null;
    const out = [];
    for (const x of v) {
        if (!isObj(x) || Object.keys(x).some((k) => !['label', 'value', 'note'].includes(k)))
            return null;
        if (!isText(x.label, DICE_TERM_LIMITS.label) || !isInt(x.value, -DICE_TERM_LIMITS.value, DICE_TERM_LIMITS.value) || x.value === 0)
            return null;
        if (x.note !== undefined && !isText(x.note, DICE_TERM_LIMITS.note))
            return null;
        out.push({ label: x.label.trim(), value: x.value, ...(typeof x.note === 'string' ? { note: x.note.trim() } : {}) });
    }
    return out;
}
export const diceSum = (terms) => (terms ?? []).reduce((n, t) => n + t.value, 0);
/** A dice line's words: "Bear: +2 dice (health spells)", "Snake: −1 die (cast in combat)". */
export function diceTermLine(t) {
    const n = Math.abs(t.value);
    return `${t.label}: ${t.value < 0 ? '−' : '+'}${n} ${n === 1 ? 'die' : 'dice'}${t.note ? ` (${t.note})` : ''}`;
}
/** The declaration, strictly: null when any part is malformed, so the forms offer none rather than wrong ones. */
export function parseMagicDice(v) {
    if (!Array.isArray(v) || v.length > 6)
        return null;
    const out = [];
    for (const s of v) {
        if (!isObj(s) || Object.keys(s).some((k) => !['field', 'when', 'items', 'only'].includes(k)) || !isKey(s.field) || !Array.isArray(s.items) || s.items.length > 12)
            return null;
        let only;
        if (s.only !== undefined) {
            const o = s.only;
            if (!isObj(o) || Object.keys(o).some((k) => k !== 'spell' && k !== 'spirit') || (o.spell !== undefined && !isKey(o.spell)) || (o.spirit !== undefined && !isKey(o.spirit)))
                return null;
            only = { ...(isKey(o.spell) ? { spell: o.spell } : {}), ...(isKey(o.spirit) ? { spirit: o.spirit } : {}) };
        }
        let when;
        if (s.when !== undefined) {
            if (!isObj(s.when) || !isKey(s.when.field) || !isText(s.when.is, 40))
                return null;
            when = { field: s.when.field, is: s.when.is };
        }
        const items = [];
        for (const i of s.items) {
            if (!isObj(i) || Object.keys(i).some((k) => !['for', 'dice', 'tn', 'note', 'when_text', 'combat', 'category', 'list', 'chosen', 'choose_one', 'via'].includes(k)))
                return null;
            if (i.for !== 'spell' && i.for !== 'spirit')
                return null;
            const dice = typeof i.dice === 'string' && /^[a-z{][a-z0-9_{}]{0,62}$/.test(i.dice) ? i.dice : undefined;
            if ((i.dice !== undefined && !dice) || (i.tn !== undefined && !isKey(i.tn)) || (dice === undefined) === (i.tn === undefined))
                return null;
            if ((i.note !== undefined && !isText(i.note, 60)) || (i.combat !== undefined && i.combat !== true))
                return null;
            for (const k of ['when_text', 'category', 'chosen', 'choose_one', 'via'])
                if (i[k] !== undefined && !isKey(i[k]))
                    return null;
            if (i.list !== undefined && (!Array.isArray(i.list) || i.list.length > 4 || !i.list.every(isKey)))
                return null;
            items.push({
                for: i.for, ...(dice ? { dice } : {}), ...(isKey(i.tn) ? { tn: i.tn } : {}), ...(typeof i.note === 'string' ? { note: i.note } : {}),
                ...(isKey(i.when_text) ? { when_text: i.when_text } : {}), ...(i.combat === true ? { combat: true } : {}),
                ...(isKey(i.category) ? { category: i.category } : {}), ...(Array.isArray(i.list) ? { list: [...i.list] } : {}),
                ...(isKey(i.chosen) ? { chosen: i.chosen } : {}), ...(isKey(i.choose_one) ? { choose_one: i.choose_one } : {}), ...(isKey(i.via) ? { via: i.via } : {}),
            });
        }
        out.push({ field: s.field, ...(when ? { when } : {}), items, ...(only ? { only } : {}) });
    }
    return out;
}
const slugOf = (ref) => ref.split('/').at(-1) ?? ref;
const fill = (s, category) => (s.includes('{category}') ? (category ? s.replace(/\{category\}/g, category.toLowerCase()) : null) : s);
/**
 * The lines a casting (`spell`, with the spell's category) or a conjuring (`spirit`, with the spirit's address) gets
 * from the sources: each item that applies, its number non-zero, as a line under the entry's name.
 */
export function magicDiceLines(decl, found, ask) {
    const out = [];
    for (const f of found) {
        const src = decl[f.source];
        if (!src)
            continue;
        const e = f.entry;
        for (const i of src.items) {
            if (i.for !== ask.for || (i.combat && !ask.combat))
                continue;
            const category = ask.for === 'spell' ? ask.category : undefined;
            if (i.category) {
                const c = e.attrs[i.category];
                if (typeof c !== 'string' || !category || c.toLowerCase() !== category.toLowerCase())
                    continue;
            }
            if (ask.for === 'spirit' && i.list) {
                if (!ask.spirit)
                    continue;
                const from = i.via ? f.via?.[i.via] : e;
                if (!from)
                    continue;
                const chosen = i.chosen && i.choose_one && e.attrs[i.choose_one] === true ? f.chosen?.[i.chosen] : undefined;
                const names = chosen !== undefined ? [slugOf(chosen)] : i.list.flatMap((k) => from.links[k] ?? []).map(slugOf);
                if (i.choose_one && e.attrs[i.choose_one] === true && chosen === undefined)
                    continue;
                if (!names.includes(slugOf(ask.spirit)))
                    continue;
            }
            const key = fill(i.dice ?? i.tn, category);
            if (!key)
                continue;
            const n = e.attrs[key];
            if (!isInt(n, -DICE_TERM_LIMITS.value, DICE_TERM_LIMITS.value) || n === 0)
                continue;
            const cond = i.when_text ? e.attrs[i.when_text] : undefined;
            const noteRaw = i.note ? fill(i.note, category) : null;
            const note = [noteRaw, typeof cond === 'string' ? cond.toLowerCase() : null].filter(Boolean).join(', ').slice(0, DICE_TERM_LIMITS.note);
            out.push({
                term: { label: e.name.slice(0, DICE_TERM_LIMITS.label), value: n, ...(note ? { note } : {}) },
                ...(i.tn ? { tn: true } : {}), ...(typeof cond === 'string' ? { ask: cond.slice(0, 60) } : {}),
            });
        }
    }
    return out;
}
// ── What an entry limits the caster to (issue 1172) ───────────────────────
const article = (w) => (/^[aeiou]/i.test(w) ? 'An' : 'A');
/** The casting limits the sources set (a source's `only.spell`), each with who it is in words. Pure. */
export function castLimits(decl, found) {
    const out = [];
    for (const f of found) {
        const src = decl[f.source];
        if (!src?.only?.spell)
            continue;
        const c = f.entry.attrs[src.only.spell];
        if (typeof c === 'string' && c.trim())
            out.push({ who: src.when ? `${article(f.entry.name)} ${f.entry.name} ${src.when.is}` : f.entry.name, category: c.trim() });
    }
    return out;
}
/** The refusal of a spell of `category` the limits rule out, in words; null when none does (or the category is unknown). */
export function limitRefusal(limits, category) {
    for (const l of limits)
        if (category && l.category.toLowerCase() !== category.toLowerCase())
            return `${l.who} casts only ${l.category} spells, not ${category} ones.`.slice(0, 200);
    return null;
}
/**
 * The refusal, in words, of a casting or a conjuring the caster's entry rules out (a source's `only`): a spell of
 * another category, a spirit its link does not name. Null when nothing rules it out (or the spell's category is unknown).
 */
export function magicRefusal(decl, found, ask) {
    if (ask.for === 'spell')
        return limitRefusal(castLimits(decl, found), ask.category);
    for (const f of found) {
        const src = decl[f.source];
        if (!src?.only?.spirit)
            continue;
        const e = f.entry;
        const who = src.when ? `${article(e.name)} ${e.name} ${src.when.is}` : e.name;
        const allowed = (e.links[src.only.spirit] ?? []).map(slugOf);
        if (allowed.length && ask.spirit && !allowed.includes(slugOf(ask.spirit)))
            return `${who} conjures only the ${allowed.map((x) => x.replace(/-/g, ' ')).join(' or ')}.`.slice(0, 200);
    }
    return null;
}
export function parseStandingPowers(v) {
    if (!isObj(v) || Object.keys(v).some((k) => k !== 'family' && k !== 'resist_test' && k !== 'resistTest'))
        return null;
    const family = v.family;
    const test = v.resist_test ?? v.resistTest;
    return isKey(family) && isKey(test) ? { family, resistTest: test } : null;
}
export const STANDING_LIMITS = { count: 24, name: 80, rule: 400, levels: 12 };
/**
 * A power row's entry read (its detail's fields and `attrs`): `table_effect` "dm" is judged by the DM; `per_level`
 * `{ test, dice }` gives that many dice a level on the test. Null when it has no name. Pure.
 */
export function standingPowerFrom(body, ref, levels) {
    if (!isObj(body))
        return null;
    const a = { ...body, ...(isObj(body.attrs) ? body.attrs : {}) };
    if (!isText(a.name, STANDING_LIMITS.name) || !isInt(levels, 1, STANDING_LIMITS.levels))
        return null;
    const per = isObj(a.per_level) ? a.per_level : {};
    const effect = typeof a.effect === 'string' && !LINE_BAD.test(a.effect) ? a.effect.trim().slice(0, STANDING_LIMITS.rule) : '';
    return {
        name: a.name.trim(), ref, levels, effect, judged: a.table_effect === 'dm',
        ...(isKey(per.test) && isInt(per.dice, 1, 6) ? { test: per.test, dice: per.dice } : {}),
    };
}
/** A list of them off the wire: each well-formed one, at most the limit. */
export function parseStandingPowerList(v) {
    if (!Array.isArray(v))
        return [];
    const out = [];
    for (const x of v.slice(0, STANDING_LIMITS.count)) {
        if (!isObj(x) || !isText(x.name, STANDING_LIMITS.name) || typeof x.ref !== 'string' || x.ref.length > 200 || !isInt(x.levels, 1, STANDING_LIMITS.levels) || typeof x.effect !== 'string' || x.effect.length > STANDING_LIMITS.rule)
            continue;
        out.push({ name: x.name, ref: x.ref, levels: x.levels, effect: x.effect, judged: x.judged === true, ...(isKey(x.test) && isInt(x.dice, 1, 6) ? { test: x.test, dice: x.dice } : {}) });
    }
    return out;
}
/** The dice lines the powers give a test: "Magic Resistance: +2 dice (spell resistance)", per level, each at most the bound. */
export function standingDice(powers, test) {
    return powers.filter((p) => p.test === test && p.dice).slice(0, DICE_TERM_LIMITS.count).map((p) => ({
        label: p.name.slice(0, DICE_TERM_LIMITS.label), value: Math.min(DICE_TERM_LIMITS.value, p.dice * p.levels), note: test.replace(/_/g, ' ').slice(0, DICE_TERM_LIMITS.note),
    }));
}
// ── Carried on the token, decided by the server (issue 1172) ──────────────
// A target's resistance dice and a caster's limit are read from the sheet when its token is synced (by the DM or the
// owner, with their own sign-in) and carried among the token's sheet numbers (`values.sheet`), which reach the DM and
// the owners alone. The cast then reads them off the tokens: the dice reach a spell's resistance even when the caster
// cannot read the target's sheet, and a limit is refused where the cast is decided, whatever the wire says.
/** A power's dice on the system's resist test, as a sheet number: its name's words, its dice. */
export const STANDING_DICE_PURPOSE = 'standing_dice';
/** A caster's limit, as a sheet number: the category first, then who it is, word by word. */
export const CASTS_ONLY_PURPOSE = 'casts_only';
const wordsOf = (s) => s.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean).map((w) => w.slice(0, 24)).slice(0, 8);
const titled = (words) => words.map((w) => `${w[0].toUpperCase()}${w.slice(1)}`).join(' ');
/** The sheet numbers that carry a sheet's resistance dice and its limits. Pure. */
export function carriedMagicNumbers(resist, limits) {
    const out = [];
    for (const t of resist.slice(0, DICE_TERM_LIMITS.count)) {
        const w = wordsOf(t.label);
        if (w.length && t.value)
            out.push({ purpose: STANDING_DICE_PURPOSE, words: w, value: t.value });
    }
    for (const l of limits.slice(0, 4)) {
        const c = wordsOf(l.category)[0];
        const w = wordsOf(l.who).slice(0, 7);
        if (c && w.length)
            out.push({ purpose: CASTS_ONLY_PURPOSE, words: [c, ...w], value: 0 });
    }
    return out;
}
/** The resistance dice a token carries, as dice lines under the resist test's words. Pure. */
export function carriedResistDice(sheet, test) {
    return (sheet ?? []).filter((n) => n.purpose === STANDING_DICE_PURPOSE && n.words.length && Number.isInteger(n.value) && n.value !== 0).slice(0, DICE_TERM_LIMITS.count).map((n) => ({
        label: titled(n.words).slice(0, DICE_TERM_LIMITS.label), value: Math.max(-DICE_TERM_LIMITS.value, Math.min(DICE_TERM_LIMITS.value, n.value)), note: test.replace(/_/g, ' ').slice(0, DICE_TERM_LIMITS.note),
    }));
}
/** The limits a token carries. Pure. */
export function carriedLimits(sheet) {
    return (sheet ?? []).filter((n) => n.purpose === CASTS_ONLY_PURPOSE && n.words.length >= 2).map((n) => ({ category: titled([n.words[0]]), who: titled(n.words.slice(1)) }));
}
/**
 * What the server takes for a cast from the tokens it is made with (issue 1172), or its refusal in words:
 * - the target's resistance dice are the ones its token carries; the DM may send their own list (a line switched off,
 *   `resistDice` present, even empty), a player's list is never read;
 * - a caster limited to one spell category is refused another; a player's cast of a spell whose category is not named
 *   is refused too, so the limit cannot be stepped round by leaving it out. The DM's is theirs to judge.
 */
export function castCarried(req, a) {
    const limits = carriedLimits(a.caster);
    const refused = limitRefusal(limits, req.spell.category);
    if (refused)
        return refused;
    if (!a.isDm && limits.length && !req.spell.category)
        return `${limits[0].who} casts only ${limits[0].category} spells: cast it from the sheet's spells, so its category is known.`.slice(0, 200);
    if (a.isDm && req.resistDice !== undefined)
        return { resistDice: req.resistDice.map((t) => ({ ...t })) };
    return { resistDice: a.resistTest ? carriedResistDice(a.target, a.resistTest) : [] };
}
//# sourceMappingURL=magicDice.js.map